/**
 * ТЗ V5 №14: группы состояния камер для фильтра в админке локации.
 *
 * status (CameraStatus) — что камере велено делать; health_summary — что
 * мониторинг видит на самом деле. RUNNING без подтверждённого ONLINE —
 * не «Онлайн». Правила идут сверху вниз, первое совпадение побеждает.
 * Это справочник интерфейса, а не серверная логика: фильтруется весь
 * полученный список локации, запросов не добавляется.
 */

export const CAMERA_STATUS_GROUPS = [
  { id: 'online', label: 'Онлайн' },
  { id: 'stopped', label: 'Остановлена' },
  { id: 'unavailable', label: 'Недоступна' },
  { id: 'pending_delete', label: 'На удалении' },
  { id: 'deleted', label: 'Удалена' },
  { id: 'transitional', label: 'Переходное состояние' },
  { id: 'new', label: 'Новая' },
  { id: 'unknown', label: 'Статус неизвестен' },
];

export const ALL_CAMERA_STATUS_GROUPS = CAMERA_STATUS_GROUPS.map((group) => group.id);

const norm = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : '');

export const classifyCameraStatusGroup = (camera) => {
  const status = norm(camera?.status);
  const health = camera?.health_summary || null;
  const connection = norm(health?.connection_status);
  const desired = health?.desired_running;

  if (status === 'PENDING_DELETE') return 'pending_delete';
  if (status === 'DELETED') return 'deleted';
  if (status === 'NEW') return 'new';
  if (['STARTING', 'STOPPING', 'RESTARTING'].includes(status)) return 'transitional';
  if (status === 'ERROR') return 'unavailable';
  if (status === 'STOPPED') return 'stopped';
  if (status === 'RUNNING') {
    if (desired === true && connection === 'ONLINE') return 'online';
    if (desired === true && connection) return 'unavailable';
    if (desired === false && connection === 'STOPPED') return 'stopped';
  }
  return 'unknown';
};

export const countCamerasByStatusGroup = (cameras) => {
  const counts = Object.fromEntries(ALL_CAMERA_STATUS_GROUPS.map((id) => [id, 0]));
  (Array.isArray(cameras) ? cameras : []).forEach((camera) => {
    counts[classifyCameraStatusGroup(camera)] += 1;
  });
  return counts;
};

/** Несколько групп — OR. Пустой выбор — пустой список, без скрытого сброса. */
export const filterCamerasByStatusGroups = (cameras, groups) => {
  const selected = new Set(groups);
  return (Array.isArray(cameras) ? cameras : [])
    .filter((camera) => selected.has(classifyCameraStatusGroup(camera)));
};

const STORAGE_PREFIX = '24inspector.admin.camera-status-filter.v1';

/** Отдельно на пользователя и локацию: выбор локации A не влияет на B. */
export const cameraStatusFilterKey = (userKey, roomId) => (
  `${STORAGE_PREFIX}.user.${String(userKey || 'anonymous').trim().toLowerCase()}.room.${roomId}`
);

/**
 * Сохранённый выбор или все группы. Битый JSON и чужой формат — значение по
 * умолчанию. Незнакомые сейчас группы не выбрасываются: их нет в локации,
 * но выбор пользователя остаётся его выбором.
 */
export const loadCameraStatusFilter = (userKey, roomId) => {
  if (roomId == null) return ALL_CAMERA_STATUS_GROUPS;
  try {
    const raw = localStorage.getItem(cameraStatusFilterKey(userKey, roomId));
    if (raw == null) return ALL_CAMERA_STATUS_GROUPS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every((id) => typeof id === 'string')) return ALL_CAMERA_STATUS_GROUPS;
    return parsed;
  } catch {
    return ALL_CAMERA_STATUS_GROUPS;
  }
};

export const saveCameraStatusFilter = (userKey, roomId, groups) => {
  if (roomId == null) return;
  try {
    localStorage.setItem(cameraStatusFilterKey(userKey, roomId), JSON.stringify(groups));
  } catch {
    // Хранилище недоступно — фильтр работает до перезагрузки.
  }
};
